// src/lib/pruefpunktLogik.js
// Gemeinsame Regeln fuer Pruefpunkte. Bewusst an EINER Stelle, damit der
// Protokoll-Editor und der Abschluss-Lueckencheck nicht auseinanderlaufen:
// Sonst wuerde der Monteur am Ende nach Fragen gefragt, die gar nicht
// angezeigt wurden.

// Blendet Folgefragen aus, die keinen Sinn ergeben (Feld sichtbar_wenn).
//   {"pruefpunkt": "05.30", "wert_in": ["ja"]}
//        nur zeigen, wenn 05.30 mit "ja" beantwortet ist
//   {"pruefpunkt": "03.4", "modifier_nicht": ["fehlt"]}
//        ausblenden, sobald bei 03.4 "fehlt" angekreuzt wurde
//   {"trafoart_vorhanden": "giessharz"}
//        nur zeigen, wenn an der Station mindestens ein solcher Trafo haengt
//   {"isolierart_in": ["sf6", "clean_air"]}
//        nur zeigen, wenn die MS-Anlage so isoliert ist (02.1 Gasdruck,
//        Migration 52). config unbekannt oder Isolierart leer -> zeigen.
//   {"os_anschluss_nicht": ["stecker"]}
//        Trafo-Kapitel: ausblenden, wenn DIESER Trafo so angeschlossen ist
//        (04.2 Erdung OS-Seite entfaellt bei Stecker, Migration 53)
//   {"vnb_enthaelt": ["netze bw", "enbw"]}
//        nur zeigen, wenn der Netzbetreiber der Station so heisst (04.10)
//   {"pruefpunkt": "05.16", "wert_nicht_in": ["n. benötigt"]}
//        ausblenden, sobald die Bezugsfrage so beantwortet ist (05.14, 05.21)
//   {"config_wahr": "nshv_vorhanden"}
//        ausblenden, wenn die Stationskonfiguration dort ausdruecklich "nein" hat
//        (05 NH-Aufsteckgriff nur mit NSHV, Migration 55)
//   {"lage_nicht": "MS_PRUEFER_ENTFAELLT"}
//        ausblenden, wenn anlagenLage() diese Aussage SICHER trifft (05.18/05.19:
//        SF6/Clean Air ohne Messfeld, alle Trafos OS gesteckt - Migration 56)
//   {"stations_typ_nicht": ["kompakt"]}
//        ausblenden bei dieser Bauform (05.28 Isoliermatte, Migration 56)
// kontext.lage = anlagenLage(config, msFelder, trafos); fehlt sie, wird nichts ausgeblendet.
// Ohne Bedingung ist ein Pruefpunkt immer sichtbar. Ist die Bezugsfrage selbst
// ausgeblendet, ist es die Folgefrage auch (NSHV nicht vorhanden -> auch keine
// Fragen zum Ueberspannungsschutz, selbst wenn dort frueher etwas stand).
//
// kontext: { config, trafo, vnbName } - alles optional. Was unbekannt ist
// (undefined), blendet nichts aus. trafo = der Trafo DIESES Kapitels (04),
// vnbName = null heisst: Station hat keinen Netzbetreiber.
export function istSichtbar(pp, allePruefpunkte, trafos, kontext, tiefe = 0) {
  const b = pp?.sichtbar_wenn;
  if (!b || typeof b !== 'object') return true;
  const config = kontext?.config;
  const kapitelTrafo = kontext?.trafo;

  // Im Trafo-Kapitel zaehlt der Trafo DIESES Kapitels (04.7 Warnschild GH nur
  // am Giessharz-Trafo, nicht an jedem Trafo einer gemischten Station).
  if (b.trafoart_vorhanden && kapitelTrafo && kapitelTrafo.trafoart) {
    const arten = Array.isArray(b.trafoart_vorhanden) ? b.trafoart_vorhanden : [b.trafoart_vorhanden];
    if (!arten.includes(kapitelTrafo.trafoart)) return false;
  }

  if (Array.isArray(b.os_anschluss_nicht) && kapitelTrafo && b.os_anschluss_nicht.includes(kapitelTrafo.os_anschluss)) {
    return false;
  }

  if (b.config_wahr && config && config[b.config_wahr] === false) return false;

  if (b.lage_nicht && kontext?.lage) {
    const flags = Array.isArray(b.lage_nicht) ? b.lage_nicht : [b.lage_nicht];
    if (flags.some(f => kontext.lage[f] === true)) return false;
  }

  if (Array.isArray(b.stations_typ_nicht) && config?.stations_typ && b.stations_typ_nicht.includes(config.stations_typ)) {
    return false;
  }

  if (Array.isArray(b.vnb_enthaelt) && kontext && kontext.vnbName !== undefined) {
    const name = String(kontext.vnbName || '').toLowerCase();
    if (!b.vnb_enthaelt.some(x => name.includes(String(x).toLowerCase()))) return false;
  }

  // trafoart_vorhanden nimmt eine Art oder eine Liste:
  //   "giessharz"                          -> Warnschild GH-Trafo
  //   ["hermetic","oel_mit_ausdehner"]     -> Oelwanne (nur bei Oel-Trafos;
  //                                           ein Giessharz-Trafo braucht keine)
  // trafos === null/undefined: Trafos (noch) unbekannt, z. B. ohne Netz und
  // ohne gemerkte Stationsdaten. Dann NICHT ausblenden - vorher verschwanden
  // das ganze Oelwannen-Kapitel und 04.7, ohne dass der Monteur wusste, warum.
  if (b.trafoart_vorhanden && Array.isArray(trafos)) {
    const arten = Array.isArray(b.trafoart_vorhanden) ? b.trafoart_vorhanden : [b.trafoart_vorhanden];
    const vorhanden = (trafos || []).some(
      t => arten.includes(t.trafoart) && t.active !== false
    );
    if (!vorhanden) return false;
  }

  if (Array.isArray(b.isolierart_in) && config && config.ms_anlage_isolierart
      && !b.isolierart_in.includes(config.ms_anlage_isolierart)) {
    return false;
  }

  if (b.pruefpunkt) {
    const bezug = (allePruefpunkte || []).find(x => x.pos_nummer === b.pruefpunkt);
    // Gibt es die Bezugsfrage nicht (anderes Modul, alter Protokollstand),
    // wird nichts versteckt - lieber eine Frage zu viel als eine zu wenig.
    if (!bezug) return true;
    if (tiefe < 5 && !istSichtbar(bezug, allePruefpunkte, trafos, kontext, tiefe + 1)) return false;
    if (Array.isArray(b.wert_in) && !b.wert_in.includes(bezug.wert_auswahl)) return false;
    if (Array.isArray(b.wert_nicht_in) && b.wert_nicht_in.includes(bezug.wert_auswahl)) return false;
    if (Array.isArray(b.modifier_nicht)) {
      const mods = Array.isArray(bezug.wert_modifiers) ? bezug.wert_modifiers : [];
      if (b.modifier_nicht.some(m => mods.includes(m))) return false;
    }
  }
  return true;
}

// Durchgezaehlte Anzeige-Nummer (Anna-Lena 18.09.2026, Kapitel 02): intern bleibt
// pos_nummer unveraendert (Hilfetexte, Regeln, alte Protokolle haengen daran),
// angezeigt wird fortlaufend ohne Luecken - faellt 02.1 Gasdruck bei einer
// Luft-Anlage weg, beginnt es trotzdem bei 02.1.
// sichtbare: NUR die sichtbaren Punkte EINES Kapitels. Ergebnis: { [pp.id]: '02.1' }.
// Kapitel 04 seit Migration 53 ebenso (04.2 entfaellt bei Stecker, 04.7 beim Oel-Trafo),
// Kapitel 05 Sicherheitszubehoer seit Migration 55 ("bitte immer die Nummerierung anpassen").
// Dort zaehlt auch der erste Punkt "05" (NH-Aufsteckgriff) mit.
// (Frueher nur Kapitel 02, 04 und 05-Sicherheitszubehoer - siehe anzeigeNummern.)

// Oelwanne je Trafo (Migration 70, Anna-Lena 22.09.2026): Das Kapitel
// 05-OEL-WANNE eines Giessharz-Trafos wird nicht angezeigt (kein Oel, keine Wanne).
// Die alte, stationsweite Form (bezugs_typ 'station') bleibt immer sichtbar.
export function kapitelAusgeblendet(modul, trafos) {
  if (modul?.modul_vorlagen?.code !== '05-OEL-WANNE' || modul?.bezugs_typ !== 'trafo') return false;
  const tr = trafoDesKapitels(modul, trafos);
  return !!tr && tr.trafoart === 'giessharz';
}

// Der Trafo, zu dem ein Kapitel gehoert (Kapitel 04 hat bezugs_id = Trafo-ID).
export function trafoDesKapitels(modul, trafos) {
  if (!modul?.bezugs_id || !Array.isArray(trafos)) return undefined;
  return trafos.find(t => t.id === modul.bezugs_id);
}
//
// Seit 21.09.2026 in ALLEN Kapiteln und auf allen Ebenen (Anna-Lena: "die
// Nummerierung stimmt nicht, bitte neu durchnummerieren"): Gezaehlt wird in der
// Reihenfolge der sichtbaren Punkte, Unterpunkte (01.3.2) haengen an ihrem
// Oberpunkt. Ist der Oberpunkt selbst kein Pruefpunkt (z. B. Gruppe 01.6) oder
// ausgeblendet, bekommt die Gruppe trotzdem eine lueckenlose Nummer. Ein Punkt
// nur aus der Kapitelnummer ("05" NH-Aufsteckgriff, "03" NSHV vorhanden) zaehlt
// als erster Punkt des Kapitels mit (wie seit Migration 55 im Kapitel 05).
// modulCode wird nicht mehr gebraucht, bleibt aber fuer die Aufrufer stehen.
export function anzeigeNummern(modulCode, sichtbare) {
  const karte = {};
  const reihe = [...(sichtbare || [])]
    .filter(pp => /^\d+(\.\d+)*$/.test(String(pp.pos_nummer || '').trim()))
    .sort((a, b) => (a.reihenfolge ?? 0) - (b.reihenfolge ?? 0));
  const neu = {};        // alte Nummer (auch Gruppe) -> neue Nummer
  const zaehler = {};    // neue Eltern-Nummer -> wie viele Kinder bisher
  const vergeben = (eltern) => { zaehler[eltern] = (zaehler[eltern] || 0) + 1; return eltern + '.' + zaehler[eltern]; };
  // Neue Nummer fuer eine alte Nummer - legt fehlende Gruppen unterwegs an.
  const nummerFuer = (alt) => {
    if (neu[alt]) return neu[alt];
    const teile = alt.split('.');
    const kapitel = teile[0];
    const eltern = teile.length <= 2 ? kapitel : nummerFuer(teile.slice(0, -1).join('.'));
    neu[alt] = vergeben(eltern);
    return neu[alt];
  };
  reihe.forEach(pp => {
    const alt = String(pp.pos_nummer).trim();
    // Reiner Kapitelpunkt ("05"): erster Punkt des Kapitels.
    karte[pp.id] = alt.includes('.') ? nummerFuer(alt) : (neu[alt] = neu[alt] || vergeben(alt));
  });
  return karte;
}
export function anzeigeNummer(pp, karte) {
  return (karte && pp && karte[pp.id]) || pp?.pos_nummer || '';
}

// Ist ein Pruefpunkt beantwortet?
export function istBeantwortet(pp) {
  // Doppeltes Ausrufezeichen: ohne es kam bei einem leeren Pruefpunkt
  // "undefined" statt false zurueck. Wirkte gleich, ist es aber nicht -
  // beim Vergleichen und Zaehlen faellt einem das irgendwann auf die Fuesse.
  return !!(pp?.wert_auswahl != null || pp?.wert_zahl != null || pp?.wert_bool != null
    || (pp?.wert_text && pp.wert_text.length > 0));
}

// --- Sicherheitsbewertung aus den Maengeln ableiten --------------------------
//
// Regeln von Anna-Lena (03./04.09.2026). Die vier Aussagen auf der
// Abschluss-Seite lauten: Personen-, Anlagen-, Betriebs- und Netzsicherheit
// "nicht gewaehrleistet".
//
// Bewusst ein VORSCHLAG, kein automatisches Setzen: Diese vier Aussagen stehen
// direkt ueber der Unterschrift des Monteurs. Die App rechnet, der Mensch
// bestaetigt. Jede Regel nennt ihren Grund, damit nachvollziehbar ist, woher
// der Vorschlag kommt.
//
// NICHT dabei, mit Absicht:
//   * Oelauffangwanne undicht (Modul 05-OEL-WANNE) - "passiert nicht, schadet
//     nur der Umwelt". Steht in der Maengelliste, loest aber keine der vier
//     Sicherheitsaussagen aus.
//   * Rein optische Punkte wie Graffiti (01.1.2) oder Regenkappen ueber den
//     Schloessern (01.3.7) - ein Mangel, aber keine Gefahr.

const P = 'personen', A = 'anlagen', B = 'betrieb', N = 'netz';

// modul: Code der Modul-Vorlage. pos: genaue Positionsnummern.
// praefix: alles, was mit einer dieser Nummern beginnt (z. B. "01.3." fuer die
// ganze Kategorie "Tueren und Schloesser"). ausser: Ausnahmen davon.
// Gruende an 01.5 (Migration 49), die die Lueftung selbst betreffen.
export const LUEFTUNG_GRUENDE = [
  'Lüftung fehlt', 'Luftdurchlass verstopft (z. B. Laub)', 'Lüftungsgitter beschädigt',
  'Tierbefall (z. B. Wespen, Vögel, Mäuse)', 'Funktion gestört (blockiert, klemmt)',
  'Korrosion oder lose Befestigung', 'Fremdkörper im Luftweg',
];

const SICHERHEITS_REGELN = [
  {
    grund: 'Sicherheitszubehör ist mangelhaft oder fehlt',
    modul: '05-SICHERHEITS-ZUBEHOER',
    setzt: [P],
  },
  {
    grund: 'Erdungsanlage ist mangelhaft',
    modul: '06-ERDERANLAGE',
    setzt: [P],
  },
  {
    grund: 'Anlage hat das Ende ihres Lebenszyklus erreicht',
    pos: ['02.12', '03.3'],
    setzt: [N, A, B],
  },
  {
    grund: 'Türen, Schlösser oder Notentriegelung mangelhaft',
    praefix: ['01.3.'],
    ausser: ['01.3.7'], // Regenkappen: Verschleissschutz, keine Gefahr
    setzt: [P],
  },
  {
    grund: 'Wasser oder Feuchtigkeit in der Station',
    pos: ['01.1.1', '01.1.3', '01.2.1', '01.2.3', '01.4.1'],
    setzt: [A, B],
  },
  {
    grund: 'Schäden am Stationsgebäude',
    pos: ['01.1', '01.1.4', '01.2.2', '01.4.2', '01.4.4', '01.4.5'],
    setzt: [A, B],
  },
  {
    // Verstopfte oder blockierte Lueftung heisst: der Trafo kann seine Waerme
    // nicht loswerden.
    grund: 'Lüftung verstopft, blockiert oder beschädigt',
    praefix: ['01.5.'],
    // Seit Migration 49 ein Punkt 01.5 mit Gruenden (wert_modifiers).
    // Ohne gewaehlten Grund zaehlt n.i.O. an 01.5 hier mit.
    // Wie frueher jeder Unterpunkt 01.5.x: JEDER Grund an 01.5 zaehlt (auch ohne Grund).
    grundPos: { '01.5': { auchOhneGrund: true } },
    setzt: [B],
  },
  {
    // Stocher-/Eindringschutz fehlt: von aussen kommt man an spannungsfuehrende
    // Teile. Gehoert zur Lueftung, ist aber eine Gefahr fuer Menschen.
    grund: 'Stocher-/Eindringschutz fehlt oder ist beschädigt',
    pos: ['01.5.8'],
    grundPos: { '01.5': { gruende: ['Stocher-/Eindringschutz fehlt oder beschädigt'] } },
    setzt: [P],
  },
  {
    // Glasbausteine/Fenster: bei einem Stoerlichtbogen fliegen sie nach aussen.
    // Hier ist "vorhanden" der Mangel (logik_invertiert), in_ordnung ist
    // deshalb schon richtig herum gedreht.
    grund: 'Fenster oder Glasbausteine in der Station',
    praefix: ['01.6.'],
    setzt: [P],
  },
  {
    grund: 'Thermografie zeigt Auffälligkeiten',
    modul: '07-ELEKTROTHERMOGRAFIE',
    setzt: [A, B],
  },
];

// Regel mit grundPos: { '01.5': { gruende: [...], auchOhneGrund } } trifft, wenn
// der Punkt n.i.O. ist und einer dieser Gruende angetippt wurde (bzw. gar keiner,
// wenn auchOhneGrund). So wirken die alten Einzelpunkte als Gruende weiter.
export function passtZuGrund(grundPos, pp) {
  const r = grundPos && grundPos[String(pp?.pos_nummer || '').trim()];
  if (!r) return false;
  const mods = Array.isArray(pp.wert_modifiers) ? pp.wert_modifiers : [];
  if (mods.length === 0) return !!r.auchOhneGrund;
  return !r.gruende || r.gruende.some(g => mods.includes(g));
}

export function sicherheitsVorschlag(pruefpunkte) {
  const gesetzt = { personen: false, anlagen: false, betrieb: false, netz: false };
  const gruende = [];

  const code = (pp) => pp?.protokoll_module?.modul_vorlagen?.code || '';
  const nichtOk = (pruefpunkte || []).filter(pp => pp.in_ordnung === false);

  for (const regel of SICHERHEITS_REGELN) {
    const trifftZu = nichtOk.some(pp => {
      const nr = pp.pos_nummer || '';
      if (regel.ausser && regel.ausser.includes(nr)) return false;
      if (regel.modul && code(pp) === regel.modul) return true;
      if (regel.pos && regel.pos.includes(nr)) return true;
      if (regel.praefix && regel.praefix.some(p => nr.startsWith(p))) return true;
      if (passtZuGrund(regel.grundPos, pp)) return true;
      return false;
    });
    if (!trifftZu) continue;
    regel.setzt.forEach(s => { gesetzt[s] = true; });
    gruende.push(regel.grund);
  }

  const werte = {
    sicherheit_personen_n_g: gesetzt.personen,
    sicherheit_anlagen_n_g:  gesetzt.anlagen,
    sicherheit_betrieb_n_g:  gesetzt.betrieb,
    sicherheit_netz_n_g:     gesetzt.netz,
  };
  return { werte, gruende, etwasGesetzt: Object.values(werte).some(Boolean) };
}

// --- Handlungsempfehlung fuer den Kundenbericht ------------------------------
//
// Formuliert aus dem, was der Monteur auf der Abschluss-Seite gesetzt hat,
// einen Absatz in ganzen Saetzen. Bewusst keine eigene Bewertung: die App
// schreibt nur aus, was bereits entschieden wurde.
// Wortwahl seit 15.09.2026 sachlicher und kundenfreundlicher (Anna-Lena:
// "die Anlage ist schlecht" klang komisch).
const ZUSTAND_SATZ = {
  '++': 'Die Anlage befindet sich insgesamt in einem sehr guten Zustand.',
  '+':  'Die Anlage befindet sich insgesamt in einem guten Zustand.',
  '0':  'Die Anlage befindet sich insgesamt in einem befriedigenden Zustand.',
  '-':  'Der Gesamtzustand der Anlage ist als mangelhaft zu bewerten.',
  '--': 'Der Gesamtzustand der Anlage ist als ungenügend zu bewerten.',
};

export function handlungsempfehlung(protokoll, maengel) {
  const p = protokoll || {};
  const offen = (maengel || []).filter(m => !m.erledigt);
  const anzahl = offen.length;
  // Maengel werden nicht eingestuft (Anna-Lena, 17.09.2026) - kein "kritisch" im Text.
  const absaetze = [];
  const imRahmen = 'Im Rahmen der ' + (p.art === 'sichtpruefung' ? 'Sichtprüfung' : 'Wartung');

  // 1 - Was wurde gefunden
  if (anzahl === 0) {
    absaetze.push(imRahmen + ' wurden keine Mängel festgestellt.');
  } else {
    const satz = anzahl === 1
      ? imRahmen + ' haben wir einen Mangel festgestellt'
      : imRahmen + ' haben wir ' + anzahl + ' Mängel festgestellt';
    absaetze.push(satz + '.');
  }

  // 2 - Zustand
  const zustand = ZUSTAND_SATZ[p.bewertung_gesamtzustand];
  if (zustand) absaetze.push(zustand);

  // 3 - Sicherheitsaussagen
  const betroffen = [];
  // Einheitlich als zusammengesetzte Woerter (Anna-Lena 21.09.2026) - "die Sicherheit
  // von Personen und die Betriebssicherheit" las sich schief.
  if (p.sicherheit_personen_n_g) betroffen.push('die Personensicherheit');
  if (p.sicherheit_anlagen_n_g)  betroffen.push('die Anlagensicherheit');
  if (p.sicherheit_betrieb_n_g)  betroffen.push('die Betriebssicherheit');
  if (p.sicherheit_netz_n_g)     betroffen.push('die Netzsicherheit');
  if (betroffen.length > 0) {
    const liste = betroffen.length === 1
      ? betroffen[0]
      : betroffen.slice(0, -1).join(', ') + ' und ' + betroffen[betroffen.length - 1];
    absaetze.push('Nach unserer fachlichen Einschätzung ' + (betroffen.length === 1 ? 'ist ' : 'sind ')
      + liste + ' derzeit nicht gewährleistet.');
  }
  // Frueher stand hier ohne Maengel "... sind gewaehrleistet". Das hat aber niemand
  // bestaetigt - ein leeres Haekchen heisst nur "nicht angekreuzt" (Anna-Lena 21.09.2026).

  // 4 - Was zu tun ist
  if (p.dringender_handlungsbedarf && anzahl === 0) {
    // Haekchen gesetzt, aber kein (sichtbarer) Mangel - kein Widerspruch im Bericht.
    absaetze.push('Aus Sicht unseres Monteurs besteht dringender Handlungsbedarf – Einzelheiten siehe Bemerkung.');
  } else if (p.dringender_handlungsbedarf) {
    absaetze.push('Wir empfehlen, die festgestellten Mängel umgehend beseitigen zu lassen.');
  } else if (p.handlungsbedarf && anzahl === 0) {
    absaetze.push('Aus Sicht unseres Monteurs besteht Handlungsbedarf – Einzelheiten siehe Bemerkung.');
  } else if (p.handlungsbedarf || ['-', '--'].includes(p.bewertung_gesamtzustand)) {
    absaetze.push('Wir empfehlen, die festgestellten Mängel zeitnah beseitigen zu lassen.');
  } else if (anzahl > 0) {
    absaetze.push('Wir empfehlen, die festgestellten Mängel im Rahmen der nächsten Instandhaltung beseitigen zu lassen.');
  } else {
    absaetze.push('Aktuell besteht kein Handlungsbedarf.');
  }

  // 5 - Naechste Wartung
  const jahre = Number(p.bewertung_wartungszyklus_jahre) || null;
  if (jahre) {
    absaetze.push('Wir empfehlen, die nächste Wartung in ' + jahre
      + (jahre === 1 ? ' Jahr' : ' Jahren') + ' durchzuführen.');
  }

  if (anzahl > 0) {
    absaetze.push('Gerne erstellen wir Ihnen ein Angebot für die Mängelbeseitigung und stehen Ihnen für Rückfragen zur Verfügung.');
  }

  return { absaetze, dringend: !!p.dringender_handlungsbedarf };
}

// --- Prueffristen beim Sicherheitszubehoer -----------------------------------
//
// Werte aus der eigenen Broschuere 15 "Sicherheitstechnisches Zubehoer in
// Transformatorstationen". Sie nennt sie ausdruecklich "Orientierungswerte aus
// DGUV Vorschrift 3 und TRBS 1201; die verbindliche Festlegung erfolgt durch
// die Gefaehrdungsbeurteilung". Die App warnt deshalb nur - sie sperrt nichts.
const FRIST_MONATE = {
  '05.4': 6,     // isolierende Schutzhandschuhe, EN 60903
  '05.8': 12,    // Spannungspruefer 230/400 V
  '05.10': 12,   // Erdungsstange
  '05.11': 12,   // Erdungs- und Kurzschliessvorrichtung
  '05.13': 12,   // HH-Sicherungszange
  '05.18': 12,   // MS-Spannungspruefer
  '05.20': 12,   // Schalt-/Isolierstange
  '05.34': 12,   // Rettungsstange (Broschuere 15: jaehrlich, DIN EN IEC 61235)
  '05.24': 12,   // Spannungsanzeigegeraet HR-System
  '05.30': 24,   // CO2-Feuerloescher
};
// Teile, bei denen nicht die Pruefung, sondern das ALTER zaehlt.
const NUTZUNGSDAUER_JAHRE = {
  '05.1': 5,     // Arbeitsschutzhelm, EN 50365
};

// Seit 13.09.2026 kann ein Datum auch nur Monat/Jahr oder nur das Jahr sein
// (DatumFeld in formui.js). Angenommen wird dann bewusst der frueheste Tag:
//   'JJJJ-MM-TT' wie gehabt, 'JJJJ-MM' -> 1. des Monats, 'JJJJ' -> 1. Januar.
// Zur Sicherheit auch deutsch 'TT.MM.JJJJ' / 'MM.JJJJ'. Sonst null.
// Frueher new Date(text): das rechnete in UTC und kannte kein 'TT.MM.JJJJ'.
export function monateSeit(datumIso) {
  if (datumIso == null) return null;
  const s = String(datumIso).trim();
  let jahr, monat = 1, tag = 1, m;
  if ((m = s.match(/^(\d{4})(?:-(\d{1,2})(?:-(\d{1,2}))?)?(?:[T ].*)?$/))) {
    jahr = Number(m[1]);
    if (m[2]) monat = Number(m[2]);
    if (m[3]) tag = Number(m[3]);
  } else if ((m = s.match(/^(?:(\d{1,2})\.)?(\d{1,2})\.(\d{4})$/))) {
    jahr = Number(m[3]);
    monat = Number(m[2]);
    if (m[1]) tag = Number(m[1]);
  } else {
    return null;
  }
  if (monat < 1 || monat > 12 || tag < 1 || tag > 31) return null;
  const d = new Date(jahr, monat - 1, tag);
  if (isNaN(d)) return null;
  const heute = new Date();
  return (heute.getFullYear() - d.getFullYear()) * 12 + (heute.getMonth() - d.getMonth());
}

// --- Soll-Stueckzahlen aus den Stammdaten (Anna-Lena 18.09.2026, Migration 56) ---
// Raeume = MS-, NS- und kombinierte MS/NS-Raeume. Kompaktstation ohne Raeume: 1.
// Unbekannt (keine Raeume eingetragen, keine Kompaktstation) -> null = nichts vorschlagen.
export function raeumeAnzahl(config) {
  if (!config) return null;
  if (config.stations_typ === 'kompakt') return 1;
  const werte = ['anzahl_ms_raeume', 'anzahl_ns_raeume', 'anzahl_ms_ns_raeume'].map(k => config[k]);
  if (werte.every(v => v == null || v === '')) return null;
  return werte.reduce((n, v) => n + (Number(v) || 0), 0);
}
// Warn- und Hinweisschilder (05.9):
//   je Raum 4 (VDE, Erste Hilfe, Brandbekaempfung, 5 Sicherheitsregeln)
//   + ab 2 Ringkabelfeldern je Ringkabelfeld "Vorsicht Rueckspannung"
//   + je MS-Feld "Nicht schalten" und "Vorsicht geerdet und kurzgeschlossen"
export function schilderSoll(config, msFelder) {
  const raeume = raeumeAnzahl(config);
  if (raeume == null) return null;
  const felder = (msFelder || []).filter(f => f.active !== false);
  const ring = felder.filter(f => f.typ_code === 'R').length;
  const teile = { grund: 4 * raeume, rueckspannung: ring >= 2 ? ring : 0, feld: 2 * felder.length };
  return { summe: teile.grund + teile.rueckspannung + teile.feld, ...teile, raeume, ring, felder: felder.length };
}
// Erdungs- und Kurzschliessvorrichtungen (05.10, Halterungen 05.12) - Anna-Lena 18.09.2026:
//   je Messfeld 2, je Giessharz-Trafo 2 (MS- und NS-Seite),
//   Oel-Trafo: OS mit Porzellan-Kerzen 1 (MS-Seite) + US mit Erdungsbolzen 1 (NS-Seite),
//   Luftanlage OHNE Erdungsschalter: je Trafofeld/Abgang (T, L, R) 1.
// Nur ein Vorschlag - "Stück soll" laesst sich ueberschreiben.
const US_MIT_BOLZEN = ['kerzen_flach_eb', 'kerzen_fahne_eb', 'klemmen_eb', 'klemmen_hauben_kugel',
  'kerzen_flach', 'kerzen_fahne', 'klemmen'];   // alte Werte ohne Angabe zaehlen mit
export function ekSoll(config, msFelder, trafos) {
  const felder = (msFelder || []).filter(f => f.active !== false);
  const tr = (trafos || []).filter(t => t.active !== false);
  if (felder.length === 0 && tr.length === 0) return null;
  const messfelder = felder.filter(f => f.typ_code === 'M').length * 2;
  let trafo = 0;
  for (const t of tr) {
    if (t.trafoart === 'giessharz') { trafo += 2; continue; }
    if (t.os_anschluss === 'kerzen') trafo += 1;
    if (US_MIT_BOLZEN.includes(t.us_anschluss)) trafo += 1;
  }
  const luft = config?.ms_anlage_isolierart === 'luft' || config?.ms_anlage_isolierart === 'luft_offen';
  const abgaenge = luft && config?.ms_erdungsschalter === false
    ? felder.filter(f => ['T', 'L', 'R'].includes(f.typ_code)).length : 0;
  return { summe: messfelder + trafo + abgaenge, messfelder, trafo, abgaenge,
           schalterUnbekannt: luft && config?.ms_erdungsschalter == null };
}
// Feuerloescher (05.30 -> 05.31): Pruefung alle 2 Jahre, von Monat zu Monat gerechnet.
export function feuerloescherBefund(letztePruefung) {
  const monate = monateSeit(letztePruefung);
  if (monate == null) return null;
  const m = String(letztePruefung).match(/^(\d{4})-(\d{1,2})/) || String(letztePruefung).match(/(\d{1,2})\.(\d{4})$/);
  let naechste = null;
  if (m) {
    const jahr = Number(m[1].length === 4 ? m[1] : m[2]);
    const monat = Number(m[1].length === 4 ? m[2] : m[1]);
    naechste = String(monat).padStart(2, '0') + '/' + (jahr + 2);
  }
  return { monate, ueberfaellig: monate > 24, naechste };
}

// Liefert einen Warntext oder null. werte = pp.zusatz_werte
// Zahl aus einem Zusatzfeld - leer bleibt leer (Number(null) waere 0).
function zahlOderNull(v) {
  if (v == null || v === '') return null;
  const n = Number(String(v).replace(',', '.'));
  return isFinite(n) ? n : null;
}

export function pruefFristHinweis(posNummer, werte) {
  const w = werte || {};
  const nr = String(posNummer || '').trim();

  const frist = FRIST_MONATE[nr];
  if (frist && w.letzte_pruefung) {
    const alt = monateSeit(w.letzte_pruefung);
    if (alt != null && alt >= frist) {
      const text = frist === 6 ? 'ein halbes Jahr'
        : frist === 12 ? 'ein Jahr'
        : (frist / 12) + ' Jahre';
      return 'Letzte Prüfung liegt ' + alt + ' Monate zurück — die Frist beträgt ' + text + '.';
    }
  }

  const dauer = NUTZUNGSDAUER_JAHRE[nr];
  if (dauer && w.baujahr) {
    const jahre = new Date().getFullYear() - Number(w.baujahr);
    if (jahre >= dauer) {
      return 'Baujahr ' + w.baujahr + ' — die übliche Nutzungsdauer von '
        + dauer + ' Jahren ist erreicht.';
    }
  }

  // Thermografie: die Uebertemperatur ausrechnen. Ohne Einstufung in
  // Warnstufen - Anna-Lena am 04.09.2026 auf Nachfrage: "nein, haben wir
  // nicht". Es gibt bei ETB also keine festen Grenzwerte; die Bewertung macht
  // der Thermograf. Die App liefert nur die Rechnung.
  const t = zahlOderNull(w.temp_c), tb = zahlOderNull(w.temp_bezug_c);
  if (t != null && tb != null) {
    const delta = Math.round((t - tb) * 10) / 10;
    const last = zahlOderNull(w.belastung_p) != null ? ' bei ' + zahlOderNull(w.belastung_p) + ' % Belastung' : '';
    return 'Übertemperatur: ' + String(delta).replace('.', ',') + ' K'
      + ' (' + String(t).replace('.', ',') + ' °C gegen '
      + String(tb).replace('.', ',') + ' °C)' + last + '.';
  }

  // Fehlbestand rechnet die App selbst aus, damit niemand subtrahieren muss.
  // Nur wenn BEIDE Werte eingetragen sind - ein geleertes "Stk. ist" ergab
  // sonst "Fehlbestand: 5 Stück (0 von 5)".
  const soll = zahlOderNull(w.stk_soll), ist = zahlOderNull(w.stk_ist);
  if (soll != null && ist != null && soll > ist) {
    return 'Fehlbestand: ' + (soll - ist) + ' Stück (' + ist + ' von ' + soll + ' vorhanden).';
  }

  return null;
}

// --- Ergebnis und Hinweis (PDF und "letzte Wartung" in der App) -------------
// 'JJJJ-MM-TT' -> 'TT.MM.JJJJ', 'JJJJ-MM' -> 'MM.JJJJ' (wie datumAnzeige in formui.js).
function fmtDatumFlexibel(d) {
  if (d == null || d === '') return '';
  const m = String(d).trim().match(/^(\d{4})(?:-(\d{1,2})(?:-(\d{1,2})(?:[T ].*)?)?)?$/);
  if (!m) return String(d);
  const zw = (x) => String(x).padStart(2, '0');
  if (m[3]) return zw(m[3]) + '.' + zw(m[2]) + '.' + m[1];
  if (m[2]) return zw(m[2]) + '.' + m[1];
  return m[1];
}

// Ergebnis und Hinweis eines Pruefpunkts getrennt. Ergebnis ist kurz (i.O.,
// n.i.O., ja, 12,5 Ω ...), alles Weitere - gewaehlte Gruende wie "fehlt",
// Zusatzwerte wie die Temperatur, die Bemerkung - steht im Hinweis.
export function ergebnisUndHinweis(pp) {
  if (!pp) return { ergebnis: '', hinweis: '' };
  const hinweise = [];
  let ergebnis = '';
  if (pp.typ === 'messwert') {
    if (pp.wert_zahl != null) ergebnis = String(pp.wert_zahl).replace('.', ',') + (pp.einheit ? ' ' + pp.einheit : '');
  } else if (pp.typ === 'boolean') {
    if (pp.wert_bool === true) ergebnis = 'ja';
    if (pp.wert_bool === false) ergebnis = 'nein';
  } else if (pp.typ === 'auswahl') {
    const w = pp.wert_auswahl ? String(pp.wert_auswahl) : '';
    // "i.O. – Schwingungsdaempfer statt Rollsperre" / "n.i.O. da nicht zulaessig":
    // kurzer Status ins Ergebnis, der Rest in den Hinweis.
    const teil = w.match(/^\s*(n\.?\s?i\.?\s?O\.?|i\.?\s?O\.?)\s*[–\-:,]?\s*(.*)$/i);
    if (teil && (pp.in_ordnung === true || pp.in_ordnung === false)) {
      ergebnis = pp.in_ordnung ? 'i.O.' : 'n.i.O.';
      if (teil[2]) hinweise.push(teil[2]);
    } else if (/^(nicht )?vorhanden$/i.test(w.trim()) && (pp.in_ordnung === true || pp.in_ordnung === false)) {
      // 01.6.1 Glasbausteine/Fenster (Migration 50): Bewertung ins Ergebnis, Antwort in den Hinweis.
      ergebnis = pp.in_ordnung ? 'i.O.' : 'n.i.O.';
      hinweise.push(w.trim());
    } else {
      ergebnis = w;
    }
    if (Array.isArray(pp.wert_modifiers) && pp.wert_modifiers.length > 0) hinweise.push(pp.wert_modifiers.join(', '));
    if (pp.zusatz_werte && typeof pp.zusatz_werte === 'object') {
      // Nur Felder, die der Pruefpunkt (noch) hat - Werte aus inzwischen
      // entfernten Feldern (z. B. alte Thermografie-Felder) nicht drucken.
      // Ohne Feldliste (sehr alte Protokolle) alle Werte wie bisher.
      const liste = Array.isArray(pp.zusatz_felder) ? pp.zusatz_felder.filter(f => f && f.key) : [];
      const eintraege = liste.length
        ? liste.map(f => [f.key, pp.zusatz_werte[f.key], f])
        : Object.entries(pp.zusatz_werte).map(([k, v]) => [k, v, null]);
      eintraege
        .filter(([, v]) => v != null && v !== '')
        .forEach(([k, v, f]) => {
          const wert = f && f.typ === 'date' ? fmtDatumFlexibel(v) : String(v).replace(/^(-?\d+)\.(\d+)$/, '$1,$2');
          const label = ((f && f.label) || k).replace(/\s*\(optional\)\s*/i, '');
          // "Temperatur (°C): 28" / "Temperatur [°C]" -> "Temperatur 28 °C"
          const einheit = label.match(/^(.*?)\s*[([]([^)\]]+)[)\]]\s*$/);
          hinweise.push(einheit ? einheit[1] + ' ' + wert + ' ' + einheit[2] : label + ': ' + wert);
        });
    }
  } else {
    ergebnis = pp.wert_text || '';
  }
  if (pp.bemerkung && String(pp.bemerkung).trim()) hinweise.push(String(pp.bemerkung).trim());
  return { ergebnis, hinweis: hinweise.join(' · ') };
}

